import { useEffect, useRef, useState } from "react";
import { BiChevronLeft, BiDownArrow, BiSearch, BiSend, BiUpArrow } from "react-icons/bi";
import { GiPaperClip } from "react-icons/gi";
import { IoClose } from "react-icons/io5";
import { LuImagePlus, LuLoader, LuMic, LuPanelRightClose, LuPanelRightOpen } from "react-icons/lu";
import { MdMessage } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import TextareaAutosize from "react-textarea-autosize";
import * as groupChatDb from "../../localDb/chat";
import * as personalChatDb from "../../localDb/personal_chat";
import Api from "../../service/http";
import { useSocket } from "../../service/SocketProvider";
import Message from "./messages/Message";

const messageLocalDb = (c) =>
  c?.chat_type === "PR" ? personalChatDb : groupChatDb;

const Messages = ({
  chat,
  setChat,
  messages,
  typingUser,
  scrollToBottom,
  loadMoreFromServer,
  moreLoading,
  showDetails,
  setShowDetails,
}) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const { socketSend } = useSocket();

  useEffect(() => {
    // If showDetails changes, we might need to adjust scroll if it's at the bottom
    const timer = setTimeout(() => {
      scrollToBottom(false);
    }, 300); // match transition duration
    return () => clearTimeout(timer);
  }, [showDetails]);

  const [reply, setReply] = useState(null);
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);

  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const renderReplyContent = () => {
    switch (reply?.message_type) {
      case "TXT":
        return (
          <p className="text-sm text-gray-800" style={{ whiteSpace: "pre-wrap" }}>
            {reply?.message}
          </p>
        );
      case "IMG":
        return <img src={reply?.media?.file} alt="replied image" className="w-16 h-16 object-cover rounded" />;
      case "VID":
        return (
          <video className="w-16 h-16 rounded" controls>
            <source src={reply?.media?.file} type="video/mp4" />
          </video>
        );
      case "AUD":
        return (
          <audio controls className="w-32">
            <source src={reply?.media?.file} type="audio/mpeg" />
          </audio>
        );
      case "PDF":
        return (
          <a href={reply?.media?.file} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline">
            View PDF
          </a>
        );
      default:
        return null;
    }
  };

  const setReplyMessage = (msg) => {
    setReply(msg);
    inputRef.current?.focus();
  };

  useEffect(() => {
    socketSend({
      type: "WebMeesageTyping",
      chat_id: chat?.id,
      user_id: user?.id,
      typing: !!message,
    });
  }, [message]);

  const [mention, setMention] = useState(false);
  const [mentionFor, setMentionFor] = useState([]);
  const [mentionUser, setMentionUser] = useState([]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const removeFile = (index) => {
    if (file && Array.isArray(file)) {
      const updatedFiles = file.filter((_, i) => i !== index);
      setFile(updatedFiles);
      if (updatedFiles?.length == 0) {
        setPreviewUrl(null);
        setCurrentIndex(0);
      } else {
        if (currentIndex > updatedFiles?.length - 1) {
          setCurrentIndex(updatedFiles?.length - 1);
        }
      }
    } else {
      setFile(null);
      setPreviewUrl(null);
      setCurrentIndex(0);
    }
  };

  const handleInputChange = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
      return;
    }
    setMessage(e.target.value);

    const lastWord = e?.target?.value?.split(" ").pop();
    if (lastWord.startsWith("@")) {
      setMention(true);
      setMentionFor(chat?.users?.filter(chatUser => chatUser?.user?.id != user?.id)?.filter((chatUser) => chatUser?.user?.first_name.toLowerCase().includes(lastWord.slice(1).toLowerCase())));
    } else {
      setMention(false);
      setMentionFor([]);
    }
  };

  const handleMentionSelect = (user) => {
    const words = message.trim().split(" ");
    words[words.length - 1] = `@${user?.user.first_name}`; // replace last word
    setMessage(words.join(" ") + " ");
    setMention(false);
    setMentionFor([]);
    if (!mentionUser?.find(m => m == user.id)) {
      setMentionUser([...mentionUser, user.user_id]);
    }
  };

  const fileUpload = (e) => {
    const selectedFile = e.target.files;
    if (!selectedFile) return;

    const filesArray = Array.from(selectedFile);
    setFile(prev => prev ? [...prev, ...filesArray] : filesArray);

    const type = selectedFile[0].type;
    if (type === "application/pdf" || type.startsWith("video/")) {
      setPreviewUrl(URL.createObjectURL(selectedFile[0]));
    } else {
      setPreviewUrl(null);
    }
  };

  const toggleRecording = async () => {
    if (recording) {
      mediaRecorder?.stop();
      setRecording(false);
      setRecordingTime(0);
      clearInterval(timerRef.current);
      mediaRecorder?.stream?.getTracks().forEach((track) => track.stop());
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream);
        const chunks = [];

        recorder.ondataavailable = (e) => chunks.push(e.data);
        recorder.onstop = () => {
          const audioBlob = new Blob(chunks, { type: "audio/webm" });
          const audioFile = new File([audioBlob], "voice-message.webm", { type: "audio/webm" });
          setFile(audioFile);
          setPreviewUrl(URL.createObjectURL(audioBlob));
        };

        recorder.start();
        setMediaRecorder(recorder);
        setRecording(true);
        timerRef.current = setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
      } catch (err) {
        alert("Microphone access denied or not supported.");
        console.error(err);
      }
    }
  };

  useEffect(() => {
    return () => {
      mediaRecorder?.stream?.getTracks().forEach((track) => track.stop());
      clearInterval(timerRef.current);
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const sendMessage = async () => {
    if (!message.trim() && !file) {
      alert("Please enter a message or select a file to send.");
      return;
    }

    setLoading(true);
    const formData = new FormData();

    if (file && Array.isArray(file)) {
      file?.forEach((f, idx) => {
        formData.append("files[]", f);
      });
    }

    if (file && file?.length > 1) {
      formData.append("message_type", "IMGS");
    } else {
      const type = file?.length ? file[0]?.type : "text/plain";
      if (type.startsWith("video")) formData.append("message_type", "VID");
      else if (type.startsWith("image")) formData.append("message_type", "IMG");
      else if (type.startsWith("audio")) formData.append("message_type", "AUD");
      else if (type === "application/pdf") formData.append("message_type", "PDF");
      else if (type === "text/plain" || type === "text/html") formData.append("message_type", "TXT");
      else formData.append("message_type", "DOC");
    }

    formData.append("chat_id", chat?.id);
    formData.append("message", message);
    if (reply?.id) formData.append("reply_id", reply?.id);
    if (mentionUser?.length) formData.append("mention_for", JSON.stringify(mentionUser));

    const result = await Api.post(`/web/chat/${chat?.id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    setMentionUser([]);

    messageLocalDb(chat).syncChatMessages(result?.data?.chat_id, result?.data);
    setChat(messageLocalDb(chat).getChat(result?.data?.chat_id));

    socketSend({
      type: "WebFetchMessage",
      user_id: user?.id,
      message: result?.data,
      isScroll: true,
    });

    setFile(null);
    setMessage("");
    setReply(null);
    setLoading(false);
  };

  useEffect(() => {
    setMessage("");
    setFile(null);
    setPreviewUrl(null);
    setReply(null);
  }, [chat?.id]);

  useEffect(() => {
    const handlePaste = (event) => {
      const target = event.target;

      const files = Array.from(event?.clipboardData?.files || []).filter((file) =>
        file.type.startsWith("image/")
      );

      if (files.length > 0) {
        if (
          target.tagName == "INPUT" ||
          target.tagName == "TEXTAREA" ||
          (target.isContentEditable === true)
        ) {
          event.preventDefault();
        }

        const dataTransfer = new DataTransfer();
        files.forEach((file) => dataTransfer.items.add(file));
        fileUpload({ target: { files: dataTransfer.files } });
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, []);

  const [search, setSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const isCtrlOrCmd = isMac ? e.metaKey : e.ctrlKey;

      if (isCtrlOrCmd && e.key.toLowerCase() === "f") {
        e.preventDefault();
        setSearch((prev) => !prev);
      }

      if (e.key === "Escape") {
        setSearch(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (search) {
      const input = document.getElementById("search");
      if (input) {
        input.focus();
      }
    }
  }, [search]);


  useEffect(() => {
    if (searchQuery.trim() === "") {
      setSearchResults([]);
      setCurrentSearchIndex(null);
      return;
    }

    const results = messages.filter((msg) =>
      msg?.message?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    setSearchResults(results);

  }, [searchQuery, messages]);

  const scrollToMessage = (index) => {
    if (!searchResults?.length) return;

    const safeIndex = (index + searchResults.length) % searchResults.length;
    setCurrentSearchIndex(safeIndex);

    const message = searchResults[safeIndex];
    const el = document.getElementById(`message-${message?.id}`);

    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });

      el.classList.add("ring", "rounded", "ring-indigo-200");

      // Clear highlight after 2s
      setTimeout(() => {
        el.classList.remove("ring", "ring-indigo-200");
      }, 2000);
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-inherit">
      {/* Chat header - Vuexy Style */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b shrink-0 bg-white border-gray-100">
        <div className="flex items-center gap-2 sm:gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="md:hidden p-1 -ml-2 rounded-full hover:bg-gray-100 text-indigo-600 transition-colors"
          >
            <BiChevronLeft size={32} />
          </button>
          <div className="relative cursor-pointer group shrink-0">
            <img
              src={chat?.image || "https://placehold.co/400"}
              className="w-10 h-10 rounded-full object-cover"
              alt="profile"
            />
            {/* <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white bg-green-500"></div> */}
          </div>
          <div>
            <h2 className="font-medium text-base text-gray-800">
              {chat?.name}
            </h2>
            {/* <p className="text-xs text-gray-500">
              Active now
            </p> */}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {search ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all bg-gray-50 border-gray-200">
              <input
                type="text"
                id="search"
                autoFocus
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent text-sm outline-none w-40 sm:w-60"
              />
              <div className="flex items-center gap-1 border-l pl-2 border-gray-300">
                <span className="text-[10px] text-gray-500 min-w-[35px]">
                  {searchResults?.length > 0 ? `${currentSearchIndex + 1}/${searchResults.length}` : '0/0'}
                </span>
                <BiUpArrow
                  className="text-gray-400 hover:text-indigo-500 cursor-pointer"
                  size={16}
                  onClick={() => scrollToMessage(currentSearchIndex - 1)}
                />
                <BiDownArrow
                  className="text-gray-400 hover:text-indigo-500 cursor-pointer"
                  size={16}
                  onClick={() => scrollToMessage(currentSearchIndex + 1)}
                />
                <IoClose 
                  className="text-gray-400 hover:text-red-500 cursor-pointer" 
                  size={18} 
                  onClick={() => {
                    setSearch(false);
                    setSearchQuery("");
                  }} 
                />
              </div>
            </div>
          ) : (
            <>
              <button 
                className="p-2 rounded-full transition-colors hover:bg-gray-100 text-gray-500"
                onClick={() => setSearch(true)}
              >
                <BiSearch size={22} />
              </button>
              <button 
                className="p-2 rounded-full transition-colors hover:bg-gray-100 text-gray-500"
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? <LuPanelRightClose size={22} /> : <LuPanelRightOpen size={22} />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Message list - Vuexy Style Background */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6 chat-list custom-scrollbar bg-[#f8f7fa]">
        {messages?.length === 0 && chat?.first_message_id === -1 ? (
          <div className="flex flex-col items-center justify-center h-full opacity-50">
            <MdMessage size={60} className="text-gray-300 mb-4" />
            <p className="text-center text-gray-500 text-sm">Start the conversation!</p>
          </div>
        ) : (
          chat?.first_message_id !== -1 && (
            <div className="flex justify-center mb-4">
              <button
                className="px-4 py-1.5 text-xs font-medium rounded-full transition-all bg-white hover:bg-gray-50 text-indigo-600 shadow-sm border border-gray-100 cursor-pointer"
                onClick={loadMoreFromServer}
                disabled={moreLoading}
              >
                {moreLoading ? <LuLoader className="w-4 h-4 animate-spin inline mr-2" /> : null}
                Load Previous Messages
              </button>
            </div>
          )
        )}
        
        {messages?.map((chatItem, i) => (
          <Message key={i} message={chatItem} isSender={chatItem?.sender?.id === user?.id} setReply={setReplyMessage} theme="light" />
        ))}
        
        {typingUser?.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 opacity-80 animate-pulse">
            <div className="flex -space-x-2">
              {typingUser.slice(0, 3).map((user, index) => (
                <img
                  key={index}
                  src={user?.dp ?? "https://placehold.co/400"}
                  alt="User"
                  className="w-5 h-5 rounded-full border-2 border-white object-cover"
                />
              ))}
            </div>
            <span className="text-[11px] text-gray-500">
              {typingUser.length > 1
                ? `${typingUser.length} users are typing...`
                : `${typingUser[0]?.first_name} is typing...`}
            </span>
          </div>
        )}
      </div>

      {mention && (
        <div className="bg-white border rounded shadow-md max-h-80 h-80 mt-1 w-60 ms-20 z-10 overflow-y-auto scrollbar-hide">
          {mentionFor
            ?.filter((chatUser) => user.id != chatUser.id)
            .map((chatUser) => (
              <div
                key={chatUser.id}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleMentionSelect(chatUser)}
              >
                <img
                  src={chatUser?.user?.dp ?? "https://placehold.co/400"}
                  alt="User"
                  className="w-6 h-6 rounded-full inline-block me-2"
                />
                <span className="text-sm font-semibold">{chatUser?.user?.first_name}</span>
              </div>
            ))}
        </div>
      )}

      {/* Reply preview */}
      {reply && (
        <div className="flex items-start gap-2 p-3 border-b bg-gray-100 relative">
          <img
            src={reply?.sender?.dp ?? "https://placehold.co/400"}
            alt="sender"
            className="w-8 h-8 rounded-full object-cover"
          />
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-700">{reply?.sender?.first_name ?? "User"}</p>
            <div>{renderReplyContent()}</div>
          </div>
          <button
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            onClick={() => setReply(null)}
          >
            <IoClose className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* File preview */}
      {file?.length > 0 && (
        <div className="flex justify-center">
          <div className="relative max-w-xs overflow-y-auto">

            {file && file?.length > 0 && file[0]?.type?.startsWith("image") && (
              <div>
                <div className="relative w-full h-60 overflow-hidden mt-2">
                  <img
                    src={URL.createObjectURL(file[currentIndex])}
                    className="w-full h-full object-cover rounded border-2 border-black"
                    alt="preview"
                  />
                </div>
                <div className="flex flex-wrap justify-center mt-2">
                  {file?.map((f, index) => (
                    <div className="relative group" key={index}>
                      <img
                        src={URL.createObjectURL(f)}
                        className={`w-12 h-12 object-cover rounded m-1 cursor-pointer ${index === currentIndex ? "border-2 border-indigo-500" : "border border-gray-300"
                          }`}
                        onClick={() => setCurrentIndex(index)}
                        alt={`file-${index}`}
                      />
                      <div
                        className="absolute top-0 right-0 bg-white rounded-full border z-10 border-red-300 hidden group-hover:flex items-center justify-center"
                        onClick={() => removeFile(index)}
                      >
                        <IoClose className="w-4 h-4" color="red" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {file?.length && file[0]?.type === "application/pdf" && <iframe src={previewUrl} className="w-full h-60" />}
            {file?.length && file[0]?.type.startsWith("video/") && (
              <video controls src={previewUrl} className="w-full h-60 rounded" />
            )}
            {file?.length && file[0]?.type.startsWith("audio/") && (
              <audio controls src={previewUrl} className="w-80 h-16 rounded" />
            )}
          </div>
        </div>
      )}

      {/* Message input area - Vuexy Style */}
      <div className="p-4 shrink-0 bg-white">
        <div className="flex items-end gap-1 sm:gap-2 p-1 sm:p-2 rounded-lg shadow-sm border transition-all bg-white border-gray-200 focus-within:border-indigo-500">
          {/* File Upload Hidden Inputs */}
          <input type="file" onChange={fileUpload} className="hidden" id="file" accept=".pdf,.doc,.docx,.txt,.gif" />
          <input type="file" onChange={fileUpload} className="hidden" id="images" accept="image/*,video/*,audio/*" multiple />

          {/* Action Icons Group */}
          <div className="flex items-center mb-1 ml-0.5 sm:ml-1">
            <button 
              className="p-1 sm:p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-500"
              onClick={() => document.getElementById("file").click()}
              title="Attach File"
            >
              <GiPaperClip className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button 
              className="p-1 sm:p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-500"
              onClick={() => document.getElementById("images").click()}
              title="Attach Image/Video"
            >
              <LuImagePlus className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          {/* Message Input or Recording Timer */}
          <div className="flex-1 min-w-0">
            {recording ? (
              <div className="flex items-center justify-between px-3 py-2 rounded-md bg-red-50" onClick={toggleRecording}>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
                  <span className="text-sm font-medium text-red-600">Recording...</span>
                </div>
                <span className="font-mono text-sm text-red-700">{formatTime(recordingTime)}</span>
              </div>
            ) : (
              <TextareaAutosize
                ref={inputRef}
                value={message}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Type your message..."
                className="w-full py-2 px-1 bg-transparent text-xs sm:text-sm outline-none resize-none max-h-32 placeholder:text-xs sm:placeholder:text-sm text-gray-800 placeholder-gray-400"
              />
            )}
          </div>

          {/* Right Action Group */}
          <div className="flex items-center mb-1 mr-0.5 sm:mr-1 gap-1">
            {!message && !recording && (
              <button 
                className="p-1 sm:p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-500"
                onClick={toggleRecording}
              >
                <LuMic className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
            
            <button
              disabled={loading || (!message && !file)}
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1 sm:py-1.5 rounded-md font-medium transition-all ${
                loading || (!message && !file)
                  ? 'bg-indigo-300 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 shadow-md active:scale-95'
              } text-white text-xs sm:text-sm`}
              onClick={sendMessage}
            >
              {loading ? (
                <LuLoader className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
              ) : (
                <>
                  <span className="hidden sm:inline">Send</span>
                  <BiSend className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
